import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { http } from '@/lib/http';
import { toast } from 'sonner';
import peerManager from '@/lib/peer-manager';

const AUTH_TOKEN_KEY = 'mobile_auth_token';
const setAuthToken = (token: string) => localStorage.setItem(AUTH_TOKEN_KEY, token);

export default function MobileAuthPage() {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleVerify = async (e?: React.FormEvent<HTMLFormElement>) => {
    if (e) {
      e.preventDefault();
    }
    if (otp.length < 6) {
      toast.error("Please enter the complete 6-character code.");
      return;
    }
    setLoading(true);
    const hostname = window.location.hostname;
    const PROXY_DOMAIN = (import.meta as any).env.VITE_PROXY_DOMAIN;
    const isTrustedZone = hostname === 'localhost' || hostname === '127.0.0.1' || (PROXY_DOMAIN && hostname.endsWith(PROXY_DOMAIN));

    if (isTrustedZone) {
      // Existing direct fetch logic...
      try {
        const response = await http<{ token: string, deviceId: string }>('/api/auth/verify-otp', {
          method: 'POST',
          body: JSON.stringify({
            otp: otp.toUpperCase(),
            deviceName: navigator.userAgent.substring(0, 100) // Simple device name
          }),
          sendProjectId: false,
        });
        setAuthToken(response.token);
        toast.success("Device paired successfully!");
        const from = location.state?.from?.pathname || '/pages/workspace/';
        navigate(from, { replace: true });
      } catch (error: any) {
        toast.error("Pairing failed", {
          description: error.message || "Please check the code and try again.",
        });
        setLoading(false);
      }
    } else {
      // New WebRTC handshake logic
      try {
        await peerManager.initiateHandshake(otp);
        toast.success("Device paired successfully!");
        const from = location.state?.from?.pathname || '/pages/workspace/';
        navigate(from, { replace: true });
        setLoading(false);
      } catch (error: any) {
        toast.error("P2P Pairing failed", { description: error.message });
        setLoading(false);
      }
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-background p-4">
      <Card className="w-full max-w-sm">
        <form onSubmit={handleVerify}>
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Pair with Desktop</CardTitle>
            <CardDescription>
              Enter the 6-character code displayed on your desktop screen.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-6">
            <InputOTP maxLength={6} value={otp} onChange={(value) => setOtp(value.toUpperCase())}>
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <Button type="submit" disabled={loading || otp.length < 6} className="w-full">
              {loading ? "Verifying..." : "Verify & Connect"}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}